import { useEffect, useState, useLayoutEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { X, ArrowRight, Sparkles } from "lucide-react";

const KEY = "vu_tour_done_v1";

export function shouldShowTour() {
    return typeof window !== "undefined" && !localStorage.getItem(KEY);
}

const MARGIN = 12;
const MOBILE_BREAKPOINT = 640;

export default function Tour({ role, onClose }) {
    const { t } = useTranslation();
    const nav = useNavigate();
    const [stepIdx, setStepIdx] = useState(0);
    const [rect, setRect] = useState(null);
    const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT);
    const [tipPos, setTipPos] = useState(null);
    const tipRef = useRef(null);
    const isStudent = role === "student";

    const steps = [
        {
            target: isStudent ? '[data-testid="sidebar-nav-classes"]' : '[data-testid="sidebar-nav-clients"]',
            title: isStudent ? t("tour.addFirstClassTitle") : t("tour.addFirstClientTitle"),
            body: isStudent ? t("tour.addFirstClassBody") : t("tour.addFirstClientBody"),
            cta: isStudent ? t("tour.openClasses") : t("tour.openClients"),
            ctaPath: isStudent ? "/classes" : "/clients",
            placement: "right",
        },
        {
            target: '[data-testid="stat-projects"]',
            title: t("tour.projectsTitle"),
            body: isStudent ? t("tour.projectsBodyStudent") : t("tour.projectsBodyPro"),
            cta: t("tour.openProjects"),
            ctaPath: "/projects",
            placement: "bottom",
        },
        {
            target: '[data-testid="stat-streak"]',
            title: t("tour.streakTitle"),
            body: t("tour.streakBody"),
            cta: t("tour.gotIt"),
            ctaPath: null,
            placement: "bottom",
        },
    ];
    const step = steps[stepIdx];

    const finish = () => {
        localStorage.setItem(KEY, "1");
        onClose?.();
    };

    const next = () => {
        if (step.ctaPath) {
            localStorage.setItem(KEY, "1");
            onClose?.();
            nav(step.ctaPath);
            return;
        }
        if (stepIdx < steps.length - 1) setStepIdx(stepIdx + 1);
        else finish();
    };

    const skipAndAdvance = () => {
        if (stepIdx < steps.length - 1) setStepIdx(stepIdx + 1);
        else finish();
    };

    // Watch viewport size
    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
        window.addEventListener("resize", handler);
        return () => window.removeEventListener("resize", handler);
    }, []);

    // Measure highlighted target
    useLayoutEffect(() => {
        const measure = () => {
            const el = document.querySelector(step.target);
            if (!el) { setRect(null); return; }
            const r = el.getBoundingClientRect();
            setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
            try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch (_e) { /* ignore */ }
        };
        measure();
        const obs = new ResizeObserver(measure);
        obs.observe(document.body);
        window.addEventListener("resize", measure);
        window.addEventListener("scroll", measure, true);
        return () => {
            obs.disconnect();
            window.removeEventListener("resize", measure);
            window.removeEventListener("scroll", measure, true);
        };
    }, [stepIdx, step.target]);

    // Position the tooltip after it renders so we can use its real size and clamp to viewport.
    useLayoutEffect(() => {
        if (!rect || isMobile || !tipRef.current) { setTipPos(null); return; }
        const tipBox = tipRef.current.getBoundingClientRect();
        const tipW = tipBox.width || 320;
        const tipH = tipBox.height || 200;
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        // Space available on each side
        const spaceR = vw - (rect.left + rect.width) - MARGIN;
        const spaceL = rect.left - MARGIN;
        const spaceB = vh - (rect.top + rect.height) - MARGIN;
        const spaceT = rect.top - MARGIN;

        // Decide placement: prefer step.placement; if it doesn't fit, flip; otherwise use whichever has most room
        const order = [step.placement || "right", "right", "bottom", "left", "top"];
        const fits = {
            right: spaceR >= tipW,
            left: spaceL >= tipW,
            bottom: spaceB >= tipH,
            top: spaceT >= tipH,
        };
        let chosen = order.find(p => fits[p]) || ["right", "bottom", "left", "top"].sort((a, b) => {
            const sa = a === "right" ? spaceR : a === "left" ? spaceL : a === "bottom" ? spaceB : spaceT;
            const sb = b === "right" ? spaceR : b === "left" ? spaceL : b === "bottom" ? spaceB : spaceT;
            return sb - sa;
        })[0];

        let top, left;
        if (chosen === "right") {
            left = rect.left + rect.width + MARGIN;
            top = rect.top + rect.height / 2 - tipH / 2;
        } else if (chosen === "left") {
            left = rect.left - tipW - MARGIN;
            top = rect.top + rect.height / 2 - tipH / 2;
        } else if (chosen === "bottom") {
            top = rect.top + rect.height + MARGIN;
            left = rect.left + rect.width / 2 - tipW / 2;
        } else {
            top = rect.top - tipH - MARGIN;
            left = rect.left + rect.width / 2 - tipW / 2;
        }
        // Clamp to viewport
        left = Math.max(MARGIN, Math.min(left, vw - tipW - MARGIN));
        top = Math.max(MARGIN, Math.min(top, vh - tipH - MARGIN));
        setTipPos({ top, left });
    }, [rect, isMobile, step.placement, stepIdx]);

    if (!rect) {
        return (
            <FallbackCard step={step} stepIdx={stepIdx} total={steps.length}
                          onNext={next} onSkip={skipAndAdvance} onClose={finish} />
        );
    }

    // Mobile: bottom-sheet card
    const tooltipStyle = isMobile
        ? { position: "fixed", left: MARGIN, right: MARGIN, bottom: MARGIN, maxHeight: `calc(100vh - ${MARGIN * 2}px)`, zIndex: 60 }
        : tipPos
            ? { position: "fixed", top: tipPos.top, left: tipPos.left, width: 320, maxHeight: `calc(100vh - ${MARGIN * 2}px)`, zIndex: 60 }
            : { position: "fixed", top: MARGIN, left: MARGIN, width: 320, opacity: 0, zIndex: 60 };

    return (
        <div data-testid="tour-overlay" className="fixed inset-0 z-[55]">
            {/* Dim with cutout for target */}
            <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: "auto" }} onClick={skipAndAdvance}>
                <defs>
                    <mask id="vu-tour-mask">
                        <rect width="100%" height="100%" fill="white" />
                        <rect x={rect.left - 6} y={rect.top - 6} width={rect.width + 12} height={rect.height + 12} rx="14" ry="14" fill="black" />
                    </mask>
                </defs>
                <rect width="100%" height="100%" fill="rgba(26,19,24,0.55)" mask="url(#vu-tour-mask)" />
                <rect
                    x={rect.left - 6}
                    y={rect.top - 6}
                    width={rect.width + 12}
                    height={rect.height + 12}
                    rx="14" ry="14"
                    fill="transparent"
                    stroke="url(#vu-tour-grad)"
                    strokeWidth="3"
                />
                <defs>
                    <linearGradient id="vu-tour-grad" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#7B2D8E" />
                        <stop offset="50%" stopColor="#E91E63" />
                        <stop offset="100%" stopColor="#FF6B35" />
                    </linearGradient>
                </defs>
            </svg>

            <div
                ref={tipRef}
                className="card-warm p-5 shadow-2xl fade-up overflow-y-auto scrollbar-thin"
                style={tooltipStyle}
                data-testid="tour-tooltip"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2">
                        <Sparkles size={14} className="text-[var(--brand-magenta)]" />
                        <div className="text-[10px] uppercase tracking-wide text-[var(--text-ink)]/55">{t("tour.step", { n: stepIdx + 1, total: steps.length })}</div>
                    </div>
                    <button onClick={finish} data-testid="tour-close" aria-label={t("tour.close")} className="text-[var(--text-ink)]/55 hover:text-[var(--brand-magenta)]">
                        <X size={16} />
                    </button>
                </div>
                <div className="font-serif text-xl mb-1">{step.title}</div>
                <div className="text-sm text-[var(--text-ink)]/70 mb-4">{step.body}</div>
                <div className="flex items-center justify-between gap-2">
                    <button onClick={finish} data-testid="tour-skip" className="text-xs text-[var(--text-ink)]/55 hover:text-[var(--brand-magenta)]">
                        {t("tour.skip")}
                    </button>
                    <div className="flex gap-2">
                        {stepIdx < steps.length - 1 && (
                            <button onClick={skipAndAdvance} data-testid="tour-next" className="btn-ghost text-xs">
                                {t("tour.next")}
                            </button>
                        )}
                        <button onClick={next} data-testid="tour-cta" className="btn-primary text-xs inline-flex items-center gap-1">
                            {step.cta} {step.ctaPath && <ArrowRight size={12} />}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function FallbackCard({ step, stepIdx, total, onNext, onSkip, onClose }) {
    const { t } = useTranslation();
    return (
        <div className="fixed inset-0 z-[55] grid place-items-center bg-black/40 p-4" data-testid="tour-overlay">
            <div className="card-warm p-6 max-w-sm w-full max-h-[90vh] overflow-y-auto scrollbar-thin fade-up" data-testid="tour-tooltip">
                <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] uppercase tracking-wide text-[var(--text-ink)]/55">{t("tour.step", { n: stepIdx + 1, total })}</div>
                    <button onClick={onClose} data-testid="tour-close" className="text-[var(--text-ink)]/55"><X size={16} /></button>
                </div>
                <div className="font-serif text-xl">{step.title}</div>
                <div className="text-sm text-[var(--text-ink)]/70 mt-1 mb-4">{step.body}</div>
                <div className="flex justify-end gap-2">
                    <button onClick={onSkip} className="btn-ghost text-xs">{t("tour.skipShort")}</button>
                    <button onClick={onNext} className="btn-primary text-xs">{step.cta}</button>
                </div>
            </div>
        </div>
    );
}
